import React, { useState, useCallback, useEffect } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  InfoWindow,
  OverlayView,
} from "@react-google-maps/api";
import axios from "axios";
import { GOOGLE_MAPS_API_KEY } from "utils/googleMaps";
import { RefreshCw, MapPin, Activity, Clock, Users, Eye, ShoppingCart } from "lucide-react";

const containerStyle = {
  width: "100%",
  height: "calc(100vh - 100px)",
  borderRadius: "12px",
  overflow: "hidden",
};

const center = {
  lat: 17.385044,
  lng: 78.486671,
};

const mapStyles = [
  {
    featureType: "all",
    elementType: "geometry.fill",
    stylers: [{ weight: "2.00" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "poi",
    elementType: "all",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#eeeeee" }],
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [{ color: "#c8d7d4" }],
  },
];

function EventsGeoMapView({ range, customDates }) {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState(null);
  const [locations, setLocations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const onLoad = useCallback((map) => setMap(map), []);
  const onUnmount = useCallback(() => setMap(null), []);

  const fetchMapData = async () => {
    setIsLoading(true);
    try {
      let url = `https://api.medstown.com/api/events/geo/map?range=${range}`;

      // Handle custom date range
      if (range === "custom" && customDates?.from && customDates?.to) {
        url = `https://api.medstown.com/api/events/geo/map?range=custom&from=${customDates.from}&to=${customDates.to}`;
      }

      const res = await axios.get(url);
      const responseData = res.data.data || {};
      
      // Extract locations and summary from nested structure
      setLocations(responseData.locations || []);
      setSummary(responseData.summary || null);
      setLastFetched(new Date().toLocaleString());
    } catch (error) {
      console.error("Error fetching map data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (range !== "custom" || (customDates?.from && customDates?.to)) {
      fetchMapData();
    }
  }, [range, customDates]);

  const getIntensityColor = (visitCount) => {
    if (visitCount >= 10) return "#ef4444"; // red
    if (visitCount >= 7) return "#f97316"; // orange
    if (visitCount >= 5) return "#eab308"; // yellow
    if (visitCount >= 3) return "#22c55e"; // green
    return "#3b82f6"; // blue
  };

  const getDotSize = (visitCount) => {
    if (visitCount >= 10) return "large";
    if (visitCount >= 5) return "medium";
    return "small";
  };

  const getRangeLabel = () => {
    const labels = {
      today: "Today",
      yesterday: "Yesterday",
      thisWeek: "This Week",
      lastWeek: "Last Week",
      thisMonth: "This Month",
      lastMonth: "Last Month",
      thisYear: "This Year",
      lastYear: "Last Year",
      custom: "Custom Range",
    };
    return labels[range] || "Today";
  };

  return isLoaded ? (
    <div className="mx-auto w-full bg-white p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Live Events Map
              </h1>
              <p className="text-gray-600">
                Real-time event distribution - {getRangeLabel()}
              </p>
            </div>
          </div>

          <button
            onClick={fetchMapData}
            disabled={isLoading}
            className="flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow-md transition-all duration-200 hover:bg-blue-700 hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span>{isLoading ? "Updating..." : "Refresh Data"}</span>
          </button>
        </div>

        {/* Stats Cards */}
        {summary && (
          <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
            <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 p-4">
              <div className="flex items-center space-x-3">
                <Activity className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-600">Total Visits</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {summary.totalVisits?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-green-100 p-4">
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-600">
                    Active Locations
                  </p>
                  <p className="text-2xl font-bold text-green-900">
                    {summary.totalPincodes || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 p-4">
              <div className="flex items-center space-x-3">
                <Users className="h-5 w-5 text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-purple-600">
                    Active Users
                  </p>
                  <p className="text-2xl font-bold text-purple-900">
                    {summary.totalActiveUsers || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-orange-100 p-4">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm font-medium text-orange-600">
                    Last Updated
                  </p>
                  <p className="text-xs font-semibold text-orange-900">
                    {lastFetched || "Loading..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="rounded-lg border bg-gray-50 p-4">
          <h3 className="mb-2 text-sm font-semibold text-gray-700">
            Visit Density Legend
          </h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="h-3 w-3 rounded-full bg-blue-500"></div>
              <span className="text-xs text-gray-600">1-2 visits</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-3 w-3 rounded-full bg-green-500"></div>
              <span className="text-xs text-gray-600">3-4 visits</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
              <span className="text-xs text-gray-600">5-6 visits</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-3 w-3 rounded-full bg-orange-500"></div>
              <span className="text-xs text-gray-600">7-9 visits</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="h-3 w-3 rounded-full bg-red-500"></div>
              <span className="text-xs text-gray-600">10+ visits</span>
            </div>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="relative overflow-hidden rounded-xl border bg-white shadow-lg">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={6}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            styles: mapStyles,
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: true,
          }}
        >
          {locations.map((item, idx) => {
            const lat = parseFloat(item.location?.lat);
            const lng = parseFloat(item.location?.lng);

            if (!lat || !lng) return null;

            return (
              <OverlayView
                key={idx}
                position={{ lat, lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div
                  className="relative transform cursor-pointer transition-transform hover:scale-110"
                  onClick={() =>
                    setSelected({
                      position: { lat, lng },
                      pincode: item.pincode,
                      totalVisits: item.totalVisits,
                      totalUsers: item.totalUniqueUsers,
                      eventBreakdown: item.eventBreakdown,
                      uniqueScreens: item.uniqueScreensVisited,
                      sessions: item.totalSessions,
                      city: item.location?.city,
                      state: item.location?.state,
                    })
                  }
                >
                  <div
                    className={`
                      rounded-full border-2 border-white shadow-lg
                      ${
                        getDotSize(item.totalVisits) === "large"
                          ? "h-6 w-6"
                          : getDotSize(item.totalVisits) === "medium"
                          ? "h-5 w-5"
                          : "h-4 w-4"
                      }
                      animate-pulse
                    `}
                    style={{
                      backgroundColor: getIntensityColor(item.totalVisits || 0),
                    }}
                  />
                  <div
                    className={`
                      absolute inset-0 animate-ping rounded-full opacity-30
                      ${
                        getDotSize(item.totalVisits) === "large"
                          ? "h-6 w-6"
                          : getDotSize(item.totalVisits) === "medium"
                          ? "h-5 w-5"
                          : "h-4 w-4"
                      }
                    `}
                    style={{
                      backgroundColor: getIntensityColor(item.totalVisits || 0),
                    }}
                  />
                </div>
              </OverlayView>
            );
          })}

          {selected && (
            <InfoWindow
              position={selected.position}
              onCloseClick={() => setSelected(null)}
              options={{
                pixelOffset: new window.google.maps.Size(0, -10),
              }}
            >
              <div className="min-w-[220px] p-3">
                <div className="mb-3 flex items-center space-x-2 border-b pb-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">
                    {selected.pincode}
                  </h3>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Activity className="h-4 w-4 text-green-600" />
                      <span className="text-sm text-gray-600">Total Visits:</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {selected.totalVisits}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Users className="h-4 w-4 text-purple-600" />
                      <span className="text-sm text-gray-600">Users:</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {selected.totalUsers}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Eye className="h-4 w-4 text-blue-600" />
                      <span className="text-sm text-gray-600">Screen Views:</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {selected.eventBreakdown?.screenViews || 0}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <ShoppingCart className="h-4 w-4 text-orange-600" />
                      <span className="text-sm text-gray-600">Product Views:</span>
                    </div>
                    <span className="font-semibold text-gray-900">
                      {selected.eventBreakdown?.productViews || 0}
                    </span>
                  </div>

                  <div className="mt-2 border-t pt-2 text-xs text-gray-500">
                    Sessions: {selected.sessions} | Screens: {selected.uniqueScreens}
                  </div>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {locations.length === 0 && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90">
            <div className="text-center">
              <MapPin className="mx-auto mb-3 h-12 w-12 text-gray-400" />
              <p className="font-medium text-gray-600">
                No location data available
              </p>
              <p className="text-sm text-gray-500">
                Try refreshing to load latest data
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="flex min-h-[600px] items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
        <p className="font-medium text-gray-600">Loading Interactive Map...</p>
        <p className="text-sm text-gray-500">Initializing Google Maps API</p>
      </div>
    </div>
  );
}

export default React.memo(EventsGeoMapView);
